import { faBuildingFlag, faCircleHalfStroke } from '@fortawesome/pro-solid-svg-icons';
import {
    activeDataTableFilters,
    type VxDataTableColumn,
    type VxDataTableFilter,
    type VxDataTableFilterField,
    type VxDataTableFilterSelectable,
    type VxDataTableFilterValue,
    type VxDataTableGroupByOption,
    type VxDataTableSection,
    type VxDataTableSort,
} from '@voxie/frontend-components';
import {
    GROUP_STATUS_ACTIVE,
    GROUP_STATUS_CLOSED,
    GROUP_STATUS_DRAFT,
    GROUP_STATUS_SUSPENDED,
    type GroupStatus,
} from '~/constants/groupStatuses';
import type {
    KioskTeamGroup,
    KioskTeamGroupFilterKey,
    KioskTeamGroupFilters,
    KioskTeamGroupGroupBy,
    KioskTeamGroupSection,
    KioskTeamGroupSectionsParams,
    KioskTeamGroupSetupRequiredValue,
    KioskTeamGroupSortField,
    KioskTeamGroupsCountParams,
    KioskTeamGroupsParams,
} from '~/services/adminTeamGroupsClient.types';
import { stringifyQueryString } from '~/utils/string';

const KIOSK_TEAM_GROUPS_PER_PAGE = 50;
const KIOSK_TEAM_GROUPS_SECTION_PER_PAGE = 25;
const KIOSK_TEAM_GROUPS_SEARCH_MAX_LENGTH = 80;
export const KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES = 10;

export type KioskTeamGroupsSort = VxDataTableSort<KioskTeamGroup>;
export type KioskTeamGroupsFilter = VxDataTableFilter<KioskTeamGroup>;
export type KioskTeamGroupsSection = VxDataTableSection<KioskTeamGroup, KioskTeamGroupGroupBy>;

export const kioskTeamGroupColumns: VxDataTableColumn<KioskTeamGroup>[] = [
    { key: 'name', label: 'Group', width: 280, sortable: true, hideable: false },
    { key: 'status', label: 'Status', width: 130, sortable: true },
    { key: 'users_count', label: 'Users', width: 90, align: 'end' },
    { key: 'org_unit_id', label: 'Organizational Unit', width: 200, sortable: true },
    { key: 'group_id', label: 'External ID', width: 200, sortable: true, visible: false },
    { key: 'created_at', label: 'Created', width: 140, sortable: true, visible: false },
];

export const kioskTeamGroupGroupByOptions: (VxDataTableGroupByOption<KioskTeamGroup> & {
    key: KioskTeamGroupGroupBy;
})[] = [
    { key: 'status', label: 'Status', icon: faCircleHalfStroke },
    { key: 'org_unit_id', label: 'Organizational Unit', icon: faBuildingFlag },
];

const defaultVisibleColumnKeys: string[] = kioskTeamGroupColumns
    .filter((column) => column.visible !== false)
    .map((column) => column.key);

export const isColumnVisible = (visibleColumns: string[] | null, key: keyof KioskTeamGroup): boolean =>
    (visibleColumns ?? defaultVisibleColumnKeys).includes(key);

export const columnsStorageKey = (teamId: number): string => `voxie:kiosk:team-groups:v1:${teamId}:columns`;

export const densityStorageKey = (teamId: number): string => `voxie:kiosk:team-groups:v1:${teamId}:density`;

export const defaultSort = { key: 'name', direction: 'asc' } as const satisfies NonNullable<
    VxDataTableSort<KioskTeamGroup>
>;

interface FilterOption<TValue extends string> extends VxDataTableFilterValue {
    value: TValue;
    label: string;
}

const NO_ORG_UNIT = 'none';

const statusOptions: FilterOption<GroupStatus>[] = [
    { value: GROUP_STATUS_DRAFT, label: 'Draft' },
    { value: GROUP_STATUS_ACTIVE, label: 'Active' },
    { value: GROUP_STATUS_SUSPENDED, label: 'Suspended' },
    { value: GROUP_STATUS_CLOSED, label: 'Closed' },
];

const groupStatuses: readonly string[] = statusOptions.map((option) => option.value);

export const isGroupStatus = (value: unknown): value is GroupStatus =>
    typeof value === 'string' && groupStatuses.includes(value);

const setupRequiredOptions: FilterOption<KioskTeamGroupSetupRequiredValue>[] = [
    { value: 'needs_setup', label: 'Needs setup' },
    { value: 'complete', label: 'Complete' },
];

export const noOrgUnitOption: FilterOption<typeof NO_ORG_UNIT> = { value: NO_ORG_UNIT, label: 'No OU' };

export const kioskTeamGroupFilterFields = (
    orgUnitSelectable?: VxDataTableFilterSelectable
): VxDataTableFilterField<KioskTeamGroup>[] => [
    { key: 'status', label: 'Status', values: statusOptions },
    { key: 'org_unit_id', label: 'Organizational Unit', values: [noOrgUnitOption], selectable: orgUnitSelectable },
    { key: 'setup_required', label: 'Setup', values: setupRequiredOptions },
];

const filterKeys: string[] = kioskTeamGroupFilterFields().map((field) => field.key);

const isFilterKey = (key: string): key is KioskTeamGroupFilterKey => filterKeys.includes(key);

export const toFilterParams = (filters: KioskTeamGroupsFilter[]): KioskTeamGroupFilters => {
    const params: KioskTeamGroupFilters = {};

    activeDataTableFilters(filters).forEach((filter) => {
        if (isFilterKey(filter.key)) {
            params[filter.key] = { operator: filter.operator, values: filter.values };
        }
    });

    return params;
};

export const filtersAllowStatus = (filters: KioskTeamGroupsFilter[], status: GroupStatus): boolean => {
    const statusFilter = toFilterParams(filters).status;

    if (statusFilter === undefined) {
        return true;
    }

    return statusFilter.values.includes(status) === (statusFilter.operator === 'in');
};

export const exceedsFilterValueLimit = (filters: KioskTeamGroupsFilter[]): boolean =>
    filters.some((filter) => filter.values.length > KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES);

export const toSearchQuery = (search: string): string =>
    Array.from(search.trim()).slice(0, KIOSK_TEAM_GROUPS_SEARCH_MAX_LENGTH).join('').trimEnd();

const toSortField = (columnKey: string): string => (columnKey === 'org_unit_id' ? 'org_unit_name' : columnKey);

const sortFields: string[] = kioskTeamGroupColumns
    .filter((column) => column.sortable)
    .map((column) => toSortField(column.key));

const isSortField = (key: string): key is KioskTeamGroupSortField => sortFields.includes(key);

type SortParams = Required<Pick<KioskTeamGroupsParams, 'sort_field' | 'sort_dir'>>;

const defaultSortParams: SortParams = { sort_field: defaultSort.key, sort_dir: defaultSort.direction };

export const toSortParams = (sort: KioskTeamGroupsSort): SortParams => {
    if (sort === null) {
        return defaultSortParams;
    }

    const sortField = toSortField(sort.key);

    if (!isSortField(sortField)) {
        return defaultSortParams;
    }

    return { sort_field: sortField, sort_dir: sort.direction };
};

export const toGroupsCountParams = (query: string, filters: KioskTeamGroupsFilter[]): KioskTeamGroupsCountParams => {
    const filterParams = toFilterParams(filters);

    return {
        ...(query === '' ? {} : { query }),
        ...(Object.keys(filterParams).length === 0 ? {} : { filters: filterParams }),
    };
};

export const toGroupsParams = (
    query: string,
    sort: KioskTeamGroupsSort,
    filters: KioskTeamGroupsFilter[]
): KioskTeamGroupsParams => ({
    ...toGroupsCountParams(query, filters),
    ...toSortParams(sort),
    per_page: KIOSK_TEAM_GROUPS_PER_PAGE,
});

export const toGroupSectionsParams = (
    query: string,
    sort: KioskTeamGroupsSort,
    filters: KioskTeamGroupsFilter[],
    groupBy: KioskTeamGroupGroupBy
): KioskTeamGroupSectionsParams => ({
    ...toGroupsCountParams(query, filters),
    ...toSortParams(sort),
    per_page: KIOSK_TEAM_GROUPS_SECTION_PER_PAGE,
    group_by: groupBy,
});

export const toSectionPageParams = (
    params: KioskTeamGroupSectionsParams,
    section: string,
    cursor: string | null
): KioskTeamGroupsParams => ({
    ...params,
    section,
    cursor,
});

const hasUnloadedRows = (section: KioskTeamGroupSection): boolean =>
    section.data === null ? section.total > 0 : section.next_cursor !== null;

const toSectionValue = (
    groupBy: KioskTeamGroupGroupBy,
    section: KioskTeamGroupSection
): KioskTeamGroupsSection['value'] => {
    if (groupBy === 'status') {
        return isGroupStatus(section.value) ? section.value : null;
    }

    return section.key === NO_ORG_UNIT ? null : Number(section.key);
};

export const toDataTableSection =
    (groupBy: KioskTeamGroupGroupBy) =>
    (section: KioskTeamGroupSection): KioskTeamGroupsSection => ({
        key: section.key,
        value: toSectionValue(groupBy, section),
        rows: section.data ?? [],
        total: section.total,
        hasMore: hasUnloadedRows(section),
        loading: false,
    });

export const sectionCursorsFrom = (sections: KioskTeamGroupSection[]): Record<string, string | null> =>
    Object.fromEntries(sections.map((section) => [section.key, section.next_cursor]));

export const sectionNamesFrom = (sections: KioskTeamGroupSection[]): Record<string, string | null> =>
    Object.fromEntries(sections.map((section) => [section.key, section.value]));

export const unloadedSectionKeys = (sections: KioskTeamGroupSection[]): string[] =>
    sections.filter((section) => section.data === null).map((section) => section.key);

export const expandedSectionKeys = (collapsed: string[], previouslyCollapsed: string[]): string[] =>
    previouslyCollapsed.filter((key) => !collapsed.includes(key));

export const awaitsRows = (section: KioskTeamGroupsSection): boolean =>
    section.rows.length === 0 && section.hasMore === true && section.loading !== true;

export const withGroupStatus = (groups: KioskTeamGroup[], groupId: string, status: GroupStatus): KioskTeamGroup[] =>
    groups.map((group) => (group.group_id === groupId ? { ...group, status } : group));

export const withSectionGroupStatus = (
    sections: KioskTeamGroupsSection[],
    groupId: string,
    status: GroupStatus
): KioskTeamGroupsSection[] =>
    sections.map((section) => ({ ...section, rows: withGroupStatus(section.rows, groupId, status) }));

const containsGroup = (section: KioskTeamGroupsSection, groupId: string): boolean =>
    section.rows.some((group) => group.group_id === groupId);

export const sectionOfGroup = (
    sections: KioskTeamGroupsSection[],
    groupId: string
): KioskTeamGroupsSection | undefined => sections.find((section) => containsGroup(section, groupId));

const withoutGroup = (section: KioskTeamGroupsSection, groupId: string): KioskTeamGroupsSection => ({
    ...section,
    rows: section.rows.filter((group) => group.group_id !== groupId),
    total: section.total - 1,
});

export const withoutSectionGroup = (sections: KioskTeamGroupsSection[], groupId: string): KioskTeamGroupsSection[] =>
    sections
        .map((section) => (containsGroup(section, groupId) ? withoutGroup(section, groupId) : section))
        .filter((section) => section.total > 0);

export const isNarrowed = (params: KioskTeamGroupsCountParams): boolean =>
    params.query !== undefined || params.filters !== undefined;

export const nextCursorFrom = (nextPageUrl: string | null): string | null =>
    nextPageUrl === null ? null : new URLSearchParams(nextPageUrl.split('?')[1]).get('cursor');

const SHORT_GROUP_ID_LENGTH = 6;

export const shortGroupId = (groupId: string): string =>
    groupId.length > SHORT_GROUP_ID_LENGTH ? `${groupId.slice(0, SHORT_GROUP_ID_LENGTH)}…` : groupId;

export const newGroupHref = (teamId: number): string =>
    `/kiosk/groups/new?${stringifyQueryString({ team_id: teamId })}`;

export const orgUnitHref = (teamId: number, orgUnitId: number): string =>
    `/kiosk/teams/${teamId}/org-units/${orgUnitId}`;

export const groupHref = (teamId: number, group: KioskTeamGroup): string =>
    group.setup_required
        ? `/kiosk/groups/new?${stringifyQueryString({ team_id: teamId, setup_group_id: group.group_id })}`
        : `/kiosk/teams/${teamId}/groups/${group.group_id}`;
